import { createApi } from '@reduxjs/toolkit/query/react';
import { getBaseQuery } from './getBaseQuery';
import { CopyProjectRequest, CopyProjectResponse, GetCurrentStageInfoRequest, GetCurrentStageInfoResponse, GetProjectsRequest, GetProjectsResponse, GetProjectsUuidRequest, GetProjectsUuidResponse, PatchProjectRequest, PatchProjectResponse, PatchSetCompletedResponse, PostCreateWithLinksRequest, PostCreateWithLinksResponse, PostProjectsRequest, PostProjectsResponse, ProjectGetCommentsRequest, ProjectGetCommentsResponse, ReturnProjectRequest, ReturnProjectResponse } from './types/TProjectServices';

const URL_Prefix = '/projects';

export const projectService = createApi({
    reducerPath: 'projectService',
    baseQuery: getBaseQuery,
    tagTypes: ['Project'],
    endpoints: (builder) => ({
        getProject: builder.query<GetProjectsResponse, GetProjectsRequest>({
            query: (params) => ({
                url: `${URL_Prefix}/`,
                method: 'GET',
                params: params,
            }),
        }),
        postProject: builder.mutation<PostProjectsResponse, PostProjectsRequest>({
            query: (body) => ({
                url: `${URL_Prefix}/`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['Project'],
        }),
        getProjectUuid: builder.query<GetProjectsUuidResponse, GetProjectsUuidRequest>({
            query: (params) => ({
                url: `${URL_Prefix}/${params.uuid}/`,
                method: 'GET',
            }),
        }),
        getProjectCurrentStageInfo: builder.query<GetCurrentStageInfoResponse, GetCurrentStageInfoRequest>({
            query: (params) => ({
                url: `${URL_Prefix}/${params.uuid}/get_current_stage_info/`,
                method: 'GET',
                params: { current_stage: params.current_stage }
            }),
        }),
        patchCreateWithLinks: builder.mutation<PostCreateWithLinksResponse, PostCreateWithLinksRequest>({
            query: (body) => ({
                url: `${URL_Prefix}/update_with_links/`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: ['Project'],
        }),
        getProjectComments: builder.query<ProjectGetCommentsResponse, ProjectGetCommentsRequest>({
            query: (params) => ({
                url: `${URL_Prefix}/get_comments/${params.project_uuid}`,
                method: 'GET',
            }),
        }),
        patchSetCompleted: builder.mutation<PatchSetCompletedResponse, PatchSetCompletedResponse>({
            query: (body) => ({
                url: `${URL_Prefix}/${body?.uuid}/set_completed/`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: ['Project'],
        }),
        patchProject: builder.mutation<PatchProjectResponse, PatchProjectRequest>({
            query: (body) => ({
                url: `${URL_Prefix}/${body?.uuid}/`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: ['Project'],
        }),
        copyProject: builder.mutation<CopyProjectResponse, CopyProjectRequest>({
            query: (body) => ({
                url: `${URL_Prefix}/${body.uuid}/copy/`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['Project'],
        }),
        returnProject: builder.mutation<ReturnProjectResponse, ReturnProjectRequest>({
            query: (body) => ({
                url: `${URL_Prefix}/${body?.uuid}/return/`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: ['Project'],
        }),
    }),
});

export const {
    useGetProjectQuery,
    useLazyGetProjectQuery,
    usePostProjectMutation,
    useGetProjectUuidQuery,
    useLazyGetProjectUuidQuery,
    useGetProjectCurrentStageInfoQuery,
    useLazyGetProjectCurrentStageInfoQuery,
    usePatchCreateWithLinksMutation,
    useGetProjectCommentsQuery,
    useLazyGetProjectCommentsQuery,
    usePatchSetCompletedMutation,
    usePatchProjectMutation,
    useCopyProjectMutation,
    useReturnProjectMutation
} = projectService;